import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import axios from 'axios';
import { debounce } from 'lodash';

interface TickerOption {
  symbol: string;
  name: string;
  type?: string;
  region?: string;
  matchScore?: number;
}

interface TickerSearchInputProps {
  value: string;
  onChange: (value: string, option?: TickerOption) => void;
  onSelect?: (option: TickerOption) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  className?: string;
  showPopularOnFocus?: boolean;
}

const TickerSearchInput: React.FC<TickerSearchInputProps> = ({
  value,
  onChange,
  onSelect,
  placeholder = "Search by company name or ticker symbol",
  required = false,
  disabled = false,
  error,
  className = "",
  showPopularOnFocus = true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<TickerOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showPopular, setShowPopular] = useState(false);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce(async (query: string) => {
      if (query.trim().length < 1) {
        setOptions([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const url = `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/ticker-search`;
        const response = await axios.get(url, { params: { q: query } });

        if (response.data.success) {
          setOptions(response.data.results);
        } else {
          setOptions([]);
        }
      } catch (error) {
        console.error('Ticker search error:', error);
        setOptions([]);
      } finally {
        setIsLoading(false);
      }
    }, 300),
    []
  );

  // Load popular tickers
  const loadPopularTickers = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/ticker-search/popular`
      );

      if (response.data.success) {
        setOptions(response.data.results);
        setShowPopular(true);
      }
    } catch (error) {
      console.error('Failed to load popular tickers:', error);
    }
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.toUpperCase();
    setSearchQuery(newValue);
    onChange(newValue);
    setSelectedIndex(-1);
    
    if (newValue.trim().length > 0) {
      setShowPopular(false);
      debouncedSearch(newValue);
    } else {
      setOptions([]);
      if (showPopularOnFocus && isOpen) {
        loadPopularTickers();
      }
    }
  };

  // Handle option selection
  const handleSelect = (option: TickerOption) => {
    onChange(option.symbol, option);
    onSelect?.(option);
    setSearchQuery(option.symbol);
    setIsOpen(false);
    setSelectedIndex(-1);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || options.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < options.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < options.length) {
          handleSelect(options[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // Handle focus
  const handleFocus = () => {
    setIsOpen(true);
    if (showPopularOnFocus && searchQuery.trim().length === 0) {
      loadPopularTickers();
    }
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync search query with value prop
  useEffect(() => {
    setSearchQuery(value);
  }, [value]);

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          className={`
            block w-full pl-10 pr-10 py-3 text-base border rounded-md
            ${error 
              ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500' 
              : 'border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500'
            }
            ${disabled ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : 'bg-white text-gray-900'}
            transition-colors duration-200
          `}
          autoComplete="off"
          autoCapitalize="characters"
        />
        {searchQuery && !disabled && (
          <button
            type="button"
            onClick={() => {
              setSearchQuery('');
              onChange('');
              setOptions([]);
              inputRef.current?.focus();
            }}
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
          </button>
        )}
        {!searchQuery && isOpen && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <ChevronDownIcon className="h-5 w-5 text-gray-400" />
          </div>
        )}
      </div>

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}

      <AnimatePresence>
        {isOpen && (options.length > 0 || isLoading || showPopular) && (
          <motion.div
            ref={dropdownRef}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"
          >
            {isLoading ? (
              <div className="px-4 py-3 text-gray-500 text-center">
                <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                <span className="ml-2">Searching...</span>
              </div>
            ) : showPopular && options.length > 0 ? (
              <>
                <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Popular Stocks
                </div>
                {options.map((option, index) => (
                  <TickerOption
                    key={option.symbol}
                    option={option}
                    isSelected={index === selectedIndex}
                    onClick={() => handleSelect(option)}
                  />
                ))}
              </>
            ) : options.length > 0 ? (
              options.map((option, index) => (
                <TickerOption
                  key={option.symbol}
                  option={option}
                  isSelected={index === selectedIndex}
                  onClick={() => handleSelect(option)}
                  showScore={true}
                />
              ))
            ) : null}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

interface TickerOptionProps {
  option: TickerOption;
  isSelected: boolean;
  onClick: () => void;
  showScore?: boolean;
}

const TickerOption: React.FC<TickerOptionProps> = ({ 
  option, 
  isSelected, 
  onClick,
  showScore = false 
}) => {
  return (
    <div
      className={`
        cursor-pointer select-none relative py-3 pl-3 pr-9 hover:bg-gray-50
        ${isSelected ? 'bg-blue-50 text-blue-900' : 'text-gray-900'}
        transition-colors duration-150
      `}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center">
            <span className={`font-semibold ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
              {option.symbol}
            </span>
            {showScore && option.matchScore && (
              <span className="ml-2 text-xs text-gray-500">
                {Math.round(option.matchScore * 100)}% match
              </span>
            )}
          </div>
          <span className={`block truncate text-sm ${isSelected ? 'text-blue-700' : 'text-gray-500'}`}>
            {option.name}
          </span>
        </div>
        {option.type && (
          <span className="ml-2 text-xs text-gray-400">
            {option.type}
          </span>
        )}
      </div>
    </div>
  );
};

export default TickerSearchInput; 